import React, {useState, useContext, useEffect, useRef, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import block from 'bem-cn-lite';
import {Alert, Button, Icon, Text} from '@gravity-ui/uikit';
import {Key, ArrowRightFromSquare, ChevronUp, ChevronDown} from '@gravity-ui/icons';
import {AppContext} from '../../../../app/context';
import {ModalChangePassword, ModalQuit} from '@components';
import {useMediaQuery} from 'react-responsive';
import {MenuItemConfig, StateBtn} from '@features/profile/types/SideBar';
import {menuItems} from './constants';
import {useMenuKeyboard} from '@hooks/keyboard';

import './SideBar.scss';

const b = block('profile-sidebar');

export const SideBar: React.FC = () => {
    const [showModalQuit, setShowModalQuit] = useState(false);
    const [showModalChangePassword, setShowModalChangePassword] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const alertTimerRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const {
        emit,
        state: {clientDetails}
    } = useContext(AppContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobileScreen = useMediaQuery({query: '(max-width: 1150px)'});
    const sizeIcon = isMobileScreen ? 16 : 20;

    const visibleMenuItems = useMemo(
        () =>
            menuItems.filter((item) => {
                if (item.id === 'AddressBtn') {
                    return clientDetails?.isPickup === false;
                }

                if (item.id === 'OfferBtn') {
                    return clientDetails?.isOfferRequired === true;
                }

                return true;
            }),
        [clientDetails?.isPickup, clientDetails?.isOfferRequired]
    );

    const [stateBtn, setStateBtn] = useState<StateBtn>({
        PersonBtn: true,
        AddressBtn: false,
        PriceBtn: false,
        FavoritePricesBtn: false,
        ContactsBtn: false,
        ConditionRefundBtn: false,
        OfferBtn: false
    });

    useEffect(() => {
        const path = location.pathname.replace('/profile/', '');
        const item = visibleMenuItems.find((m) => m.pathMatch === path);
        if (item) {
            toggleActiveBtn(item.id);
        }
    }, [location, visibleMenuItems]);

    useEffect(() => {
        return () => {
            if (alertTimerRef.current) {
                clearTimeout(alertTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                isExpanded &&
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsExpanded(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExpanded]);

    function toggleActiveBtn(id: string) {
        if (id in stateBtn) {
            const newState = Object.keys(stateBtn).reduce((acc, key) => {
                acc[key as keyof StateBtn] = key === id;
                return acc;
            }, {} as StateBtn);
            setStateBtn(newState);
        }
    }

    function handleMenuItemClick(item: MenuItemConfig, e?: React.MouseEvent) {
        e?.stopPropagation();
        toggleActiveBtn(item.id);
        navigate(item.route);
        setIsExpanded(false);
    }

    function handleClickChangePassword(e?: React.MouseEvent) {
        e?.stopPropagation();
        setShowModalChangePassword((prev) => !prev);
        setIsExpanded(false);
    }

    function handleClickLogout(e?: React.MouseEvent) {
        e?.stopPropagation();
        setShowModalQuit((prev) => !prev);
        setIsExpanded(false);
    }

    function handleContainerClick() {
        setIsExpanded((prev) => !prev);
    }

    const handlePasswordChangeSuccess = () => {
        setShowAlert(true);

        if (alertTimerRef.current) {
            clearTimeout(alertTimerRef.current);
        }

        alertTimerRef.current = setTimeout(() => {
            setShowAlert(false);
            alertTimerRef.current = null;
        }, 10_000);
    };

    const activeItem = visibleMenuItems.find((item) => stateBtn[item.id]) || visibleMenuItems[0];
    const inactiveItems = visibleMenuItems.filter((item) => !stateBtn[item.id]);
    const mobileMenuActions = [
        ...inactiveItems.map((item) => ({type: 'nav' as const, item})),
        {type: 'password' as const, item: null},
        {type: 'logout' as const, item: null}
    ];
    const {highlightedIndex: mobileMenuIndex} = useMenuKeyboard({
        isOpen: isMobileScreen && isExpanded,
        items: mobileMenuActions,
        onSelect: (action) => {
            if (action.type === 'nav' && action.item) {
                handleMenuItemClick(action.item);
            } else if (action.type === 'password') {
                handleClickChangePassword();
            } else {
                handleClickLogout();
            }
        },
        onClose: () => setIsExpanded(false)
    });

    return (
        <>
            {/* Мобильная версия */}
            {isMobileScreen ? (
                <div ref={containerRef} className={b({mobile: true, expanded: isExpanded})}>
                    <div
                        className={b('mobile-header')}
                        onClick={handleContainerClick}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleContainerClick();
                            }
                            if (event.key === 'Escape' && isExpanded) {
                                event.preventDefault();
                                setIsExpanded(false);
                            }
                        }}
                    >
                        <Button className={b('mobile-active-item')} size="m" selected>
                            <Icon data={activeItem.icon} size={sizeIcon} />
                            <Text variant="body-1">{activeItem.label}</Text>
                        </Button>
                        <Button view="normal-contrast" size="m">
                            <Icon data={isExpanded ? ChevronUp : ChevronDown} />
                        </Button>
                    </div>

                    {isExpanded && (
                        <div className={b('mobile-content')}>
                            <div className={b('mobile-items')}>
                                {inactiveItems.map((item, index) => (
                                    <Button
                                        key={item.id}
                                        className={b('mobile-item', {
                                            highlighted: mobileMenuIndex === index
                                        })}
                                        size="m"
                                        view="flat"
                                        onClick={(e) => handleMenuItemClick(item, e)}
                                    >
                                        <Icon data={item.icon} size={sizeIcon} />
                                        <Text variant="body-1">{item.label}</Text>
                                    </Button>
                                ))}
                            </div>
                            <div className={b('mobile-footer')}>
                                <Button
                                    size="m"
                                    view="normal"
                                    onClick={handleClickChangePassword}
                                    className={
                                        mobileMenuIndex === inactiveItems.length
                                            ? b('mobile-item', {highlighted: true})
                                            : undefined
                                    }
                                >
                                    <Icon data={Key} size={sizeIcon} />
                                    Изменить пароль
                                </Button>
                                <Button
                                    size="m"
                                    view="flat-danger"
                                    onClick={handleClickLogout}
                                    className={
                                        mobileMenuIndex === inactiveItems.length + 1
                                            ? b('mobile-item', {highlighted: true})
                                            : undefined
                                    }
                                >
                                    <Icon data={ArrowRightFromSquare} size={sizeIcon} />
                                    Выйти из профиля
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* Десктопная версия */
                <div className={b()}>
                    <div className={b('menu-items')}>
                        {visibleMenuItems.map((item) => (
                            <Button
                                key={item.id}
                                className={b('button', {primary: true})}
                                size="xl"
                                onClick={() => handleMenuItemClick(item)}
                                selected={stateBtn[item.id]}
                                id={item.id}
                            >
                                <Icon data={item.icon} size={sizeIcon} />
                                {item.label}
                            </Button>
                        ))}
                    </div>
                    <div className={b('menu-footer')}>
                        <Button
                            className={b('button', {small: true})}
                            size="xl"
                            onClick={handleClickChangePassword}
                        >
                            <Icon data={Key} size={sizeIcon} />
                            Изменить пароль
                        </Button>
                        <Button
                            className={b('button', {logout: true, small: true})}
                            size="xl"
                            onClick={handleClickLogout}
                        >
                            <Icon data={ArrowRightFromSquare} size={sizeIcon} />
                            Выйти из профиля
                        </Button>
                    </div>
                </div>
            )}

            <ModalChangePassword
                open={showModalChangePassword}
                onClose={handleClickChangePassword}
                onChangePasswordSuccess={handlePasswordChangeSuccess}
                positiveAction={() => emit('logout', {redirectTo: '/login'})}
            />

            <ModalQuit
                visibility={showModalQuit}
                stateModal={setShowModalQuit}
                positiveAction={() => emit('logout', {redirectTo: '/'})}
            />

            {showAlert && (
                <Alert
                    theme="success"
                    className={b('alert')}
                    message={<Text variant="subheader-2">Пароль успешно изменён</Text>}
                />
            )}
        </>
    );
};
